'client-side';

import crypto from 'crypto';
import jsCookies from 'js-cookie';// Used only for deleting cookies.
import { AuthenticationResultType, CognitoIdentityProviderClient, CognitoIdentityProviderServiceException, GetUserCommand, GetUserCommandOutput, InitiateAuthCommand } from '@aws-sdk/client-cognito-identity-provider';
import Logger from '@/utils/logger/base';

const REGION = 'us-east-1';
const CLIENT_ID = process.env.AWS_COGNITO_CLIENT_ID || '';
const CLIENT_SECRET = process.env.AWS_COGNITO_CLIENT_SECRET || '';
const COOKIE_KEY_PREFIX = 'pbskids.auth.cognito';

const loginClient = new CognitoIdentityProviderClient({ region: REGION });
const logger = new Logger({ caller: 'managers.authentication.cognito.login' });

export enum AUTH_COOKIES {
  'AccessToken' = `${COOKIE_KEY_PREFIX}.AccessToken`,
  'IdToken' = `${COOKIE_KEY_PREFIX}.IdToken`,
  'Email' = `${COOKIE_KEY_PREFIX}.Email`,
  'RefreshToken' = `${COOKIE_KEY_PREFIX}.RefreshToken`,
  'Username' = `${COOKIE_KEY_PREFIX}.Username`,
}

type AuthenticationErrorType = {
  email: string;
  message: string;
  type: string;
}

type AuthenticationResponse = {
  authResult?: AuthenticationResultType;
  email?: string;
  error?: AuthenticationErrorType;
  userResult?: GetUserCommandOutput & {
    resaveCookies?: boolean;
  };
}

async function generateSecretHash(username: string) {
  if (!CLIENT_ID) {
    throw Error('`CLIENT_ID` is not defined for this environment. Cannot proceed with login request.');
  }

  if (!CLIENT_SECRET) {
    throw Error('`CLIENT_SECRET` is not defined for this environment. Cannot proceed with login request.');
  }

  return crypto.createHmac('sha256', CLIENT_SECRET).update(`${username}${CLIENT_ID}`).digest('base64');
};

async function loginRequest(email: string, command: InitiateAuthCommand): Promise<AuthenticationResponse> {
  let authError: AuthenticationErrorType;

  try {
    const response = await loginClient.send(command);
    logger.debug('loginRequest', { response });

    if (response.AuthenticationResult) {
      // Get user attributes.
      const userResponse = await loginClient.send(
        new GetUserCommand({ AccessToken: response.AuthenticationResult.AccessToken }),
      );

      return {
        authResult: response.AuthenticationResult,
        userResult: userResponse,
        email,
      };
    }

    authError = {
      type: 'CUSTOM_NO_AUTHENTICATION_RESULT',
      message: 'No AuthenticationResult in response',
      email,
    };
  } catch (error) {
    const err = error as CognitoIdentityProviderServiceException;
    authError = {
      type: err.name || 'CUSTOM_UNKNOWN_AUTHENTICATION_ERROR',
      message: err.message || error as string || '',
      email,
    };
  }

  // Remove CLIENT_ID from error message
  authError.message = authError.message.replaceAll(CLIENT_ID, '**CLIENT_ID**');

  // Remove CLIENT_SECRET from error message
  authError.message = authError.message.replaceAll(CLIENT_SECRET, '**CLIENT_SECRET**');

  logger.error('Login Failed', authError);
  return {
    error: authError,
  };
};

export async function loginWithEmailPassword(email: string, password: string): Promise<AuthenticationResponse> {
  const command = new InitiateAuthCommand({
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: {
      USERNAME: email,
      SECRET_HASH: await generateSecretHash(email),
      PASSWORD: password,
    },
  });

  return await loginRequest(email, command);
}

export async function loginWithRefreshToken(email: string, username: string, refreshToken: string):
Promise<AuthenticationResponse> {
  const command = new InitiateAuthCommand({
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: {
      REFRESH_TOKEN: refreshToken,
      SECRET_HASH: await generateSecretHash(username),
    },
  });

  const authResponse = await loginRequest(email, command);

  if (authResponse.error) {
    return authResponse;
  }

  return authResponse;
}

export function getAuthCookie(cookies?: Partial<{ [key: string]: string; }>, key?: AUTH_COOKIES): string {
  if (!cookies || !key) return '';
  return cookies[key] || '';
}

export function getContentAccessHeaders(idToken: string, accessToken: string) {
  return {
    'x-pbs-kids-content-access-token': accessToken,
    'x-pbs-kids-content-id-token': idToken,
    'x-pbs-kids-force-cache-skip': 'true',
  };
}

export async function getUser(accessToken: string): Promise<GetUserCommandOutput | null> {
  if (accessToken) {
    try {
      const response = await loginClient.send(new GetUserCommand({ AccessToken: accessToken }));
      return response;
    } catch (error) {
      logger.warn('getUser', { error });
    }
  }

  return null;
}

export function generateCookieHeaders(data: AuthenticationResponse): string[] {
  const expiryRefreshToken = 3600 * 24 * 5;// Default is 5 Days for Refresh and Username cookies.
  const expiryOtherTokens = (data.authResult?.ExpiresIn || 3600) * 0.75;// For all other cookies, 75% of expiration.
  const authCookieArr = [
    /* eslint-disable no-multi-spaces */
    // Auth Cookies
    `${AUTH_COOKIES.AccessToken }=${data.authResult?.AccessToken};Path=/;Max-Age=${expiryOtherTokens}`,
    `${AUTH_COOKIES.IdToken     }=${data.authResult?.IdToken    };Path=/;Max-Age=${expiryOtherTokens}`,

    // Refresh Auth Cookies
    `${AUTH_COOKIES.Email       }=${data.email                  };Path=/;Max-Age=${expiryRefreshToken}`,
    `${AUTH_COOKIES.Username    }=${data.userResult?.Username   };Path=/;Max-Age=${expiryRefreshToken}`,
    /* eslint-enable no-multi-spaces */
  ];

  if (data.authResult?.RefreshToken) {
    // The refreshToken is only found in the authResult on initial login, not on login refresh.
    authCookieArr.push(
      `${AUTH_COOKIES.RefreshToken}=${data.authResult?.RefreshToken};Path=/;Max-Age=${expiryRefreshToken}`,
    );
  }

  return authCookieArr;
}

export function deleteAuthCookies() {
  jsCookies.remove(AUTH_COOKIES.AccessToken, { path: '/' });
  jsCookies.remove(AUTH_COOKIES.IdToken, { path: '/' });
  jsCookies.remove(AUTH_COOKIES.RefreshToken, { path: '/' });
  jsCookies.remove(AUTH_COOKIES.Email, { path: '/' });
  jsCookies.remove(AUTH_COOKIES.Username, { path: '/' });
}
